import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ListChecks, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Field, Loading, EmptyState } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const NONE = "none";
const TYPES = [["standard", "Standard clean"], ["deep", "Deep clean"], ["move", "Move-in/out"], ["custom", "Custom"]];

function TemplateDialog({ open, onOpenChange, tpl, clients, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setF(tpl
        ? { name: tpl.name, type: tpl.type, client_id: tpl.client_id || NONE, rooms: tpl.rooms.map((r) => ({ name: r.name, text: r.tasks.join("\n") })) }
        : { name: "", type: "custom", client_id: NONE, rooms: [{ name: "Kitchen", text: "" }] });
    }
  }, [open, tpl]);

  if (!f) return null;
  const setRoom = (i, k, v) => setF((x) => ({ ...x, rooms: x.rooms.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const payload = { name: f.name, type: f.type, client_id: f.client_id === NONE ? null : f.client_id, rooms: f.rooms.map((r) => ({ name: r.name, tasks: r.text.split("\n").map((t) => t.trim()).filter(Boolean) })) };
    try {
      tpl ? await api.put(`/checklist-templates/${tpl.id}`, payload) : await api.post("/checklist-templates", payload);
      toast.success("Checklist saved");
      onSaved(); onOpenChange(false);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle className="font-display">{tpl ? "Edit checklist" : "New checklist"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Name" htmlFor="t-name"><Input id="t-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11" data-testid="template-name-input" /></Field>
            <Field label="Type"><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className="h-11" data-testid="template-type-select"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Client-specific"><Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}><SelectTrigger className="h-11" data-testid="template-client-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={NONE}>All clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></Field>
          </div>
          {f.rooms.map((r, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`template-room-${i}`}>
              <div className="flex gap-2">
                <Input placeholder="Room name" required value={r.name} onChange={(e) => setRoom(i, "name", e.target.value)} className="h-11" data-testid={`room-name-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="h-11 w-11" disabled={f.rooms.length === 1} onClick={() => setF({ ...f, rooms: f.rooms.filter((_, j) => j !== i) })} aria-label="Remove room" data-testid={`remove-room-${i}`}><X className="h-4 w-4" /></Button>
              </div>
              <Textarea rows={3} placeholder="One task per line" value={r.text} onChange={(e) => setRoom(i, "text", e.target.value)} data-testid={`room-tasks-${i}`} />
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => setF({ ...f, rooms: [...f.rooms, { name: "", text: "" }] })} data-testid="add-room-btn"><Plus className="h-4 w-4 mr-1" />Add room</Button>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="template-error">{error}</p>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="template-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save checklist</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Checklists() {
  const [list, setList] = useState(null);
  const [clients, setClients] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/checklist-templates").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const remove = async (id) => {
    try { await api.delete(`/checklist-templates/${id}`); toast.success("Checklist deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="checklists-page">
      <PageHeader title="Checklists" subtitle="Reusable room-by-room task lists for your jobs." actions={<Button className="h-11" onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" />New checklist</Button>} />
      {!list ? <Loading /> : list.length === 0 ? <EmptyState icon={ListChecks} title="No checklists yet" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-display font-semibold text-slate-900">{t.name}</div><div className="text-xs text-slate-500 mt-0.5">{t.client_name ? `For ${t.client_name}` : "All clients"} · {t.rooms.reduce((s, r) => s + r.tasks.length, 0)} tasks</div></div>
                <div className="flex">
                  <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => { setEdit(t); setOpen(true); }} aria-label="Edit" data-testid={`edit-template-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => remove(t.id)} aria-label="Delete" data-testid={`delete-template-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">{t.rooms.map((r) => <span key={r.name} className="text-xs rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{r.name} · {r.tasks.length}</span>)}</div>
            </div>
          ))}
        </div>
      )}
      <TemplateDialog open={open} onOpenChange={setOpen} tpl={edit} clients={clients} onSaved={load} />
    </div>
  );
}
